import type { ConnectionImportResult, SavedConnection, SavedConnectionProtocol } from '@/types/connection.types';

function normalizeProtocol(value: unknown): SavedConnectionProtocol {
  const raw = String(value || '').toLowerCase();

  if (raw === 'r2' || raw.includes('cloudflare')) return 'r2';
  if (raw === 'dreamhost-object' || raw.includes('dreamhost')) return 'dreamhost-object';
  if (raw === 'minio') return 'minio';
  if (raw === 'sftp' || raw.includes('ssh')) return 'sftp';
  return 's3';
}

function normalizeLabels(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  if (typeof value === 'string') return value.split(',').map((item) => item.trim()).filter(Boolean);
  return [];
}

function pickString(...values: unknown[]): string | undefined {
  const value = values.find((item) => typeof item === 'string' && item.trim());
  return typeof value === 'string' ? value.trim() : undefined;
}

function pickNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
}

function toConnection(candidate: Record<string, unknown>): Partial<SavedConnection> {
  const protocol = normalizeProtocol(candidate.protocol || candidate.type || candidate.scheme);

  return {
    nickname: pickString(candidate.nickname, candidate.name, candidate.label) || 'Imported Connection',
    protocol,
    labels: normalizeLabels(candidate.labels || candidate.tags || candidate.group),
    endpoint: pickString(candidate.endpoint, candidate.url, candidate.server, candidate.host) || '',
    port: pickNumber(candidate.port),
    bucket: pickString(candidate.bucket, candidate.container),
    region: pickString(candidate.region) || (protocol === 'r2' ? 'auto' : undefined),
    prefix: pickString(candidate.prefix, candidate.path, candidate.rootPath) || '',
    username: pickString(candidate.username, candidate.user),
    accessKeyId: pickString(candidate.accessKeyId, candidate.accessKey, candidate.username),
    secretAccessKey: pickString(candidate.secretAccessKey, candidate.secretKey, candidate.password),
    password: pickString(candidate.password),
    sshPrivateKeyName: pickString(candidate.sshPrivateKeyName, candidate.privateKey, candidate.sshKey),
    forcePathStyle: Boolean(candidate.forcePathStyle),
  };
}

export function parseImportedConnection(raw: string): ConnectionImportResult {
  const warnings: string[] = [];

  try {
    const parsed = JSON.parse(raw);
    const candidate = Array.isArray(parsed)
      ? parsed[0]
      : parsed?.connection && typeof parsed.connection === 'object'
        ? parsed.connection
        : parsed;

    if (!candidate || typeof candidate !== 'object') {
      return { connection: null, warnings: ['Import file does not contain a usable connection object.'] };
    }

    const connection = toConnection(candidate);

    if (!connection.endpoint) warnings.push('Endpoint/server was not found in the import. Fill it in before saving.');
    if (connection.protocol === 'sftp' && !connection.password && !connection.sshPrivateKeyName) {
      warnings.push('SFTP import did not include a password or SSH key reference.');
    }
    if (connection.protocol !== 'sftp' && !connection.secretAccessKey) {
      warnings.push('S3-compatible import did not include a secret key.');
    }

    return { connection, warnings };
  } catch {
    return { connection: null, warnings: ['Import file is not valid JSON.'] };
  }
}
